"use client";

import { useCallback, useEffect, useState } from "react";
import { postJson } from "@lib/http";
import type { ScenarioCard as ScenarioCardData } from "@lib/agent/conversation/types";
import { GhostButton } from "@ui/buttons";
import { useTranslations } from "@i18n/provider";
import type { ConversationDifficulty, ConversationPersona } from "../types";
import { ScenarioCard } from "./ScenarioCard";

type ScenariosResponse = {
  scenarios: ScenarioCardData[];
  defaultDifficulty: ConversationDifficulty;
};

type ScenarioPickerProps = {
  onSelectScenario: (
    persona: ConversationPersona,
    scenarioTitle: string,
    sessionId: number,
    difficulty: ConversationDifficulty,
  ) => void;
};

async function fetchScenarios(): Promise<ScenariosResponse | null> {
  const res = await postJson("/api/agent/conversation/scenarios", {});
  if (!res.ok) return null;
  return (await res.json()) as ScenariosResponse;
}

async function createSession(
  card: ScenarioCardData,
  difficulty: ConversationDifficulty,
): Promise<number | null> {
  const res = await postJson("/api/agent/conversation/scenarios", {
    selectedScenarioId: card.scenarioId,
    persona: card.persona,
    scenarioTitle: card.title,
    difficulty,
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { sessionId: number };
  return data.sessionId;
}

export function ScenarioPicker({ onSelectScenario }: ScenarioPickerProps) {
  const { t } = useTranslations();
  const [scenarios, setScenarios] = useState<ScenarioCardData[]>([]);
  const [defaultDifficulty, setDefaultDifficulty] = useState<ConversationDifficulty>("standard");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [customText, setCustomText] = useState("");
  const [customLoading, setCustomLoading] = useState(false);

  const loadScenarios = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchScenarios();
      if (!data) {
        setError(true);
        return;
      }
      setScenarios(data.scenarios);
      setDefaultDifficulty(data.defaultDifficulty);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await fetchScenarios();
        if (cancelled) return;
        if (!data) {
          setError(true);
          return;
        }
        setScenarios(data.scenarios);
        setDefaultDifficulty(data.defaultDifficulty);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleCardSelect(card: ScenarioCardData, difficulty: ConversationDifficulty) {
    setSelectedId(card.scenarioId);
    const sessionId = await createSession(card, difficulty);
    if (sessionId == null) {
      setError(true);
      setSelectedId(null);
      return;
    }
    onSelectScenario(card.persona, card.title, sessionId, difficulty);
  }

  async function handleCustomBuild() {
    const text = customText.trim();
    if (!text) return;
    setCustomLoading(true);
    setError(false);
    try {
      const genRes = await postJson("/api/agent/conversation/scenarios", {
        customScenarioText: text,
        difficulty: defaultDifficulty,
      });
      if (!genRes.ok) {
        setError(true);
        return;
      }
      const genData = (await genRes.json()) as ScenariosResponse;
      const card = genData.scenarios[0];
      if (!card) {
        setError(true);
        return;
      }
      await handleCardSelect(card, defaultDifficulty);
    } catch {
      setError(true);
    } finally {
      setCustomLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-3 px-3 py-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="shimmer-bar h-[72px] rounded-2xl" />
        ))}
        <p className="text-center text-sm text-ink-muted">
          {t("agent.conversation.picker.loading")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-3 py-3">
      <p className="eyebrow text-center text-ink-faint">{t("agent.conversation.picker.eyebrow")}</p>

      {error ? (
        <div className="rounded-2xl bg-writing-tint/40 p-3 text-sm text-writing-fg" role="alert">
          <p>{t("agent.conversation.error.scenarios")}</p>
          <button
            type="button"
            onClick={() => void loadScenarios()}
            className="mt-2 font-semibold underline"
          >
            {t("common.retry")}
          </button>
        </div>
      ) : null}

      {scenarios.length === 0 && !error ? (
        <p className="text-center text-sm italic text-ink-muted">
          {t("agent.conversation.picker.empty")}
        </p>
      ) : (
        scenarios.map((card) => (
          <ScenarioCard
            key={card.scenarioId}
            card={card}
            defaultDifficulty={defaultDifficulty}
            selected={selectedId === card.scenarioId}
            loading={selectedId !== null}
            onSelect={handleCardSelect}
          />
        ))
      )}

      <div className="mt-1">
        <label className="eyebrow mb-2 block text-ink-faint" htmlFor="custom-scenario">
          {t("agent.conversation.picker.customLabel")}
        </label>
        <textarea
          id="custom-scenario"
          rows={2}
          value={customText}
          onChange={(e) => setCustomText(e.target.value)}
          placeholder={t("agent.conversation.picker.customPlaceholder")}
          className="inset-field w-full resize-none py-2 text-sm"
          style={{ maxHeight: "60px" }}
          disabled={customLoading || selectedId !== null}
        />
        <GhostButton
          onClick={() => void handleCustomBuild()}
          disabled={!customText.trim() || customLoading || selectedId !== null}
          className="mt-2 min-h-[44px] w-full"
        >
          {customLoading
            ? t("agent.conversation.picker.loading")
            : t("agent.conversation.picker.customSubmit")}
        </GhostButton>
      </div>
    </div>
  );
}
